import { useState } from 'react'
import { FbModal } from 'components/UI/Modal'

/** The name + description the user confirmed in the dialog. */
export type SaveResult = { name: string; description: string }

/**
 * First-Bite-branded save dialog for reports and dashboards — replaces the raw
 * `window.prompt` so naming a saved view looks like the rest of the product.
 *
 * Two save paths, mirrored across reports and dashboards:
 *   - overwrite ("Save"): update the existing saved item in place. Only offered
 *     when `canOverwrite` (the thing being saved is already a saved copy, not a
 *     shipped template/dashboard).
 *   - copy ("Save as new"): persist a new saved item. Always available.
 *
 * Mounts only while `open`, so each open reseeds the inputs from the current
 * `defaultName`/`defaultDescription` (no effect-based reset needed).
 */
export function SaveDialog(props: {
  open: boolean
  onClose: () => void
  /** Noun for the title/buttons, e.g. "report" or "dashboard". */
  kind: string
  defaultName: string
  defaultDescription?: string
  /** Hide the description field (e.g. pipeline reports carry their own shell). */
  showDescription?: boolean
  /** Offer "Save" (overwrite in place) alongside "Save as new". */
  canOverwrite?: boolean
  /** Persist a new saved copy. */
  onSaveNew: (result: SaveResult) => void
  /** Overwrite the existing saved item (only called when `canOverwrite`). */
  onOverwrite?: (result: SaveResult) => void
  /** Override the title (e.g. "Duplicate report"); defaults to a Save title. */
  titleOverride?: string
  /** Override the primary button label (e.g. "Duplicate"). */
  acceptTextOverride?: string
}) {
  if (!props.open) return null
  return <SaveDialogInner {...props} />
}

function SaveDialogInner({
  onClose,
  kind,
  defaultName,
  defaultDescription = '',
  showDescription = true,
  canOverwrite = false,
  onSaveNew,
  onOverwrite,
  titleOverride,
  acceptTextOverride,
}: Parameters<typeof SaveDialog>[0]) {
  const [name, setName] = useState(defaultName)
  const [description, setDescription] = useState(defaultDescription)
  const trimmed = name.trim()
  const result = (): SaveResult => ({
    name: trimmed,
    description: description.trim(),
  })

  return (
    <FbModal
      open
      onOpenChange={(o) => !o && onClose()}
      title={
        titleOverride ?? (canOverwrite ? `Save ${kind}` : `Save ${kind} as`)
      }
      size="md"
      // Accept is the safe/primary action: overwrite when allowed, else the new
      // save. The alt button carries the secondary "Save as new" when both exist.
      acceptButtonText={
        acceptTextOverride ?? (canOverwrite ? 'Save' : 'Save as new')
      }
      blockAccept={!trimmed}
      onAccept={() => {
        if (!trimmed) return
        if (canOverwrite) onOverwrite?.(result())
        else onSaveNew(result())
        onClose()
      }}
      altButtonText={canOverwrite ? 'Save as new' : undefined}
      blockAlt={!trimmed}
      onAlt={
        canOverwrite
          ? () => {
              if (!trimmed) return
              onSaveNew(result())
              onClose()
            }
          : undefined
      }
      cancelButtonText="Cancel"
      onCancel={onClose}
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-fg-secondary">Name</span>
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`Untitled ${kind}`}
            aria-label={`${kind} name`}
            className="rounded-md border border-line bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-brand-active"
          />
        </label>
        {showDescription && (
          <label className="flex flex-col gap-1">
            <span className="text-xs text-fg-secondary">
              Description (optional)
            </span>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this shows"
              aria-label={`${kind} description`}
              className="rounded-md border border-line bg-surface px-3 py-2 text-sm text-fg outline-none focus:border-brand-active"
            />
          </label>
        )}
      </div>
    </FbModal>
  )
}
